<template>
  <div class="container-central">
    <h1 class="mt-5 mb-4">ENVIAR NEWSLETTER</h1>

    <!-- Formulario de composición -->
    <b-card class="mb-4">
      <b-form @submit.prevent="confirmarEnvio">
        <!-- Asunto -->
        <b-form-group label="Asunto del correo *" label-for="asunto">
          <b-form-input
            id="asunto"
            v-model.trim="form.asunto"
            placeholder="Escribe el asunto del email"
            :state="form.asunto.length > 0 ? true : null"
            required
          />
        </b-form-group>

        <!-- Filtro de idioma + contador -->
        <b-form-group label="Destinatarios *" label-for="idioma">
          <b-input-group>
            <b-form-select
              id="idioma"
              v-model="form.idioma_destino"
              :options="idiomaOpciones"
              @change="actualizarContador"
            />
            <b-input-group-append>
              <b-input-group-text>
                <b-spinner v-if="cargandoContador" small />
                <span v-else>
                  <strong>{{ totalDestinatarios }}</strong>&nbsp;destinatarios
                </span>
              </b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Editor HTML -->
        <b-form-group label="Contenido HTML *">
          <b-form-textarea
            v-model="form.html_content"
            placeholder="Pega aquí el HTML del email..."
            rows="12"
            style="font-family: monospace; font-size: 12px;"
            @input="actualizarPreview"
          />
        </b-form-group>

        <!-- Acciones -->
        <div class="d-flex justify-content-between align-items-center">
          <b-button variant="outline-primary" @click="togglePreview">
            <b-icon :icon="mostrarPreview ? 'eye-slash' : 'eye'" />
            {{ mostrarPreview ? 'Ocultar preview' : 'Ver preview' }}
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="!formValido || enviando"
          >
            <b-spinner v-if="enviando" small class="mr-1" />
            {{ enviando ? 'Enviando...' : 'Enviar campaña' }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Preview iframe -->
    <b-collapse v-model="mostrarPreview">
      <b-card class="mb-4">
        <template #header>
          <strong>Preview del email</strong>
        </template>
        <iframe
          ref="previewFrame"
          class="preview-iframe"
          sandbox="allow-same-origin"
          title="Preview del email"
        />
      </b-card>
    </b-collapse>

    <!-- Historial de campañas -->
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <strong>Historial de campañas enviadas</strong>
          <b-spinner v-if="cargandoHistorial" small />
        </div>
      </template>

      <b-table
        striped
        hover
        :items="campanas"
        :fields="camposTabla"
        :busy="cargandoHistorial"
        show-empty
        empty-text="No hay campañas enviadas aún"
        responsive="sm"
      >
        <template #cell(created)="data">
          {{ $moment(data.item.created).format('DD/MM/YYYY HH:mm') }}
        </template>
        <template #cell(idioma_destino)="data">
          <b-badge :variant="badgeIdioma(data.item.idioma_destino)">
            {{ labelIdioma(data.item.idioma_destino) }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-icon
            icon="arrow-repeat"
            variant="primary"
            scale="1.4"
            style="cursor: pointer"
            v-b-tooltip.hover
            title="Reutilizar como plantilla"
            @click="cargarCampana(data.item.id)"
          />
        </template>
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle" />
            <strong style="margin-left: 5px">Cargando...</strong>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import NewsletterService from "@/services/newsletter.service";
import EventBus from "@/services/eventBus.js";

export default {
  name: "NewsletterSendAdmin",
  data() {
    return {
      newsletterService: null,
      form: {
        asunto: "",
        html_content: "",
        idioma_destino: "all",
      },
      idiomaOpciones: [
        { value: "all",     text: "Todos los idiomas" },
        { value: "spanish", text: "Solo español" },
        { value: "english", text: "Solo inglés" },
      ],
      totalDestinatarios: 0,
      cargandoContador: false,
      todosLosSuscriptores: [],
      mostrarPreview: false,
      enviando: false,
      campanas: [],
      cargandoHistorial: false,
      camposTabla: [
        { key: "asunto",         label: "Asunto",        sortable: true },
        { key: "idioma_destino", label: "Idioma",        sortable: true },
        { key: "total_enviados", label: "Enviados",      sortable: true },
        { key: "created",        label: "Fecha",         sortable: true },
        { key: "actions",        label: "Reutilizar",    sortable: false },
      ],
    };
  },
  computed: {
    formValido() {
      return (
        this.form.asunto.trim().length > 0 &&
        this.form.html_content.trim().length > 0 &&
        this.totalDestinatarios > 0
      );
    },
  },
  created() {
    this.newsletterService = new NewsletterService();
  },
  mounted() {
    this.cargarSuscriptores();
    this.cargarHistorial();
  },
  methods: {
    cargarSuscriptores() {
      this.cargandoContador = true;
      this.newsletterService
        .getAll()
        .then((res) => {
          this.todosLosSuscriptores = res.data.data || [];
          this.actualizarContador();
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "No se pudo cargar la lista de suscriptores",
          });
        })
        .finally(() => {
          this.cargandoContador = false;
        });
    },
    actualizarContador() {
      if (this.form.idioma_destino === "all") {
        this.totalDestinatarios = this.todosLosSuscriptores.length;
      } else {
        this.totalDestinatarios = this.todosLosSuscriptores.filter(
          (s) => s.idioma === this.form.idioma_destino
        ).length;
      }
    },
    actualizarPreview() {
      if (!this.mostrarPreview) return;
      this.renderizarPreview();
    },
    togglePreview() {
      this.mostrarPreview = !this.mostrarPreview;
      if (this.mostrarPreview) {
        this.$nextTick(() => this.renderizarPreview());
      }
    },
    renderizarPreview() {
      const iframe = this.$refs.previewFrame;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(this.form.html_content || "<p style='color:#999;text-align:center;padding:40px'>El preview aparecerá aquí</p>");
      doc.close();
    },
    confirmarEnvio() {
      EventBus.$emit("SHOW_MODAL_QUESTION", {
        titleModal: "Confirmar envío",
        messageModal: `¿Seguro que deseas enviar esta campaña a ${this.totalDestinatarios} destinatarios?`,
        action: "sendCampana",
        from: "newslettersend",
      });
    },
    ejecutarEnvio() {
      this.enviando = true;
      this.newsletterService
        .sendCampana({ ...this.form })
        .then((res) => {
          if (res.data.status === 200) {
            EventBus.$emit("MOSTRAR_TOAST", {
              title: "Campaña enviada",
              variant: "success",
              message: `Se enviaron ${res.data.total_enviados} correos correctamente`,
            });
            this.resetForm();
            this.cargarHistorial();
          }
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "Ocurrió un error al enviar la campaña",
          });
        })
        .finally(() => {
          this.enviando = false;
        });
    },
    resetForm() {
      this.form.asunto = "";
      this.form.html_content = "";
      this.form.idioma_destino = "all";
      this.mostrarPreview = false;
      this.actualizarContador();
    },
    cargarHistorial() {
      this.cargandoHistorial = true;
      this.newsletterService
        .getCampanas()
        .then((res) => {
          this.campanas = res.data.data || [];
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "No se pudo cargar el historial de campañas",
          });
        })
        .finally(() => {
          this.cargandoHistorial = false;
        });
    },
    cargarCampana(id) {
      this.newsletterService
        .getCampana(id)
        .then((res) => {
          if (res.data.status === 200) {
            const c = res.data.data;
            this.form.asunto = c.asunto;
            this.form.html_content = c.html_content;
            this.form.idioma_destino = c.idioma_destino;
            this.actualizarContador();
            window.scrollTo({ top: 0, behavior: "smooth" });
            EventBus.$emit("MOSTRAR_TOAST", {
              title: "Plantilla cargada",
              variant: "info",
              message: "Campaña cargada como plantilla. Puedes editarla antes de enviar.",
            });
          }
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "No se pudo cargar la campaña",
          });
        });
    },
    labelIdioma(val) {
      const map = { all: "Todos", spanish: "Español", english: "English" };
      return map[val] || val;
    },
    badgeIdioma(val) {
      const map = { all: "primary", spanish: "success", english: "info" };
      return map[val] || "secondary";
    },
  },
  // Escucha la confirmación del modal de pregunta
  beforeMount() {
    EventBus.$on("RESPONSE_MODAL_QUESTION", (payLoad) => {
      if (payLoad.from === "newslettersend" && payLoad.action === "sendCampana") {
        this.ejecutarEnvio();
      }
    });
  },
  beforeDestroy() {
    EventBus.$off("RESPONSE_MODAL_QUESTION");
  },
};
</script>

<style lang="scss" scoped>
.container-central {
  width: 100%;
  min-height: 700px;
  padding: 0 20px 40px;
}

.preview-iframe {
  width: 100%;
  height: 500px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
}
</style>
